import React, { useState } from "react";
import ImageDropZone from "../components/ImageDropZone";
import SingleImageUploader from "../components/SingleImageUploader";
import { generateImagePDF } from "../services/pdfImageService";

const NID_SIDE_BY_SIDE = "nid_side_by_side";

// --- NID Pair Uploader ---------------------------------------------------
const NidPairUploader = ({ pairs, onChange, bgColor }) => {
	const update = (pairIndex, fields) => {
		const updated = [...pairs];
		updated[pairIndex] = { ...updated[pairIndex], ...fields };
		onChange(updated);
	};

	const handleCountChange = (pairIndex, e) => {
		const val = Math.min(28, Math.max(1, parseInt(e.target.value) || 1));
		update(pairIndex, { count: val });
	};

	const addPair = () => onChange([...pairs, { front: null, back: null, count: 1 }]);
	const removePair = (index) => onChange(pairs.filter((_, i) => i !== index));

	return (
		<div>
			{/* Pairs grid — fills available space, min 360px per pair card */}
			<div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}>
				{pairs.map((pair, index) => (
					<div key={index} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">

						{/* Images — zero gap, flush */}
						<div className="flex">
							<div className="flex flex-col">
								{/* <p className="text-[10px] font-medium text-gray-400 px-1 pt-1">Front</p> */}
								<SingleImageUploader
									bgColor={bgColor}
									label="Upload front"
									position="left"
									onChange={(file) => update(index, { front: file })}
								/>
							</div>
							<div className="flex flex-col">
								{/* <p className="text-[10px] font-medium text-gray-400 px-1 pt-1">Back</p> */}
								<SingleImageUploader
									bgColor={bgColor}
									label="Upload back"
									position="right"
									onChange={(file) => update(index, { back: file })}
								/>
							</div>
						</div>

						{/* Footer — pair title + count in 2 cols */}
						<div className="grid grid-cols-2 items-center gap-2 px-2 py-1.5 bg-white border-t border-gray-200">
							<div className="flex items-center gap-1.5">
								<span className="text-[11px] text-gray-500 font-medium">Pair {index + 1}</span>
								{pairs.length > 1 && (
									<button
										type="button"
										onClick={() => removePair(index)}
										className="text-red-400 hover:text-red-600 text-[10px] leading-none"
										title="Remove pair"
									>
										✕
									</button>
								)}
							</div>
							<div className="flex items-center gap-1 justify-end">
								<label className="text-[11px] text-gray-400">Count</label>
								<input
									type="number"
									min={1}
									max={28}
									value={pair.count ?? 1}
									onChange={(e) => handleCountChange(index, e)}
									className="w-12 border border-gray-300 rounded px-1.5 py-0.5 text-xs focus:ring focus:ring-blue-500 focus:outline-none bg-white"
								/>
							</div>
						</div>

					</div>
				))}
			</div>

			{/* Add pair */}
			<button
				type="button"
				onClick={addPair}
				className="mt-3 w-full border-2 border-dashed border-blue-300 text-blue-500 hover:bg-blue-50 py-2 rounded-lg text-sm font-medium transition-colors"
			>
				+ Add Another Pair
			</button>
		</div>
	);
};

// --- Main Component -------------------------------------------------------
const ImageUploader = () => {
	const [files, setFiles] = useState([]);
	const [count, setCount] = useState(4);
	const [size, setSize] = useState("landscape");
	const [bgColor, setBgColor] = useState("#ffffff");
	const [nidPairs, setNidPairs] = useState([{ front: null, back: null, count: 1 }]);

	const presetBGColors = ["#ffffff", "#007bff", "#add8e6", "#d3d3d3", "#00bcd4"];
	const isNidSideBySide = size === NID_SIDE_BY_SIDE;

	const handleCreatePdf = async () => {
		if (isNidSideBySide) {
			for (const pair of nidPairs) {
				if (!pair.front || !pair.back) {
					return alert("Please select both Front and Back images for every pair.");
				}
			}
			await generateImagePDF({ files: nidPairs, count, size, bgColor });
		} else {
			if (!files.length) return alert("No images selected");
			await generateImagePDF({ files, count, size, bgColor });
		}
	};

	return (
		<div className="flex flex-col lg:flex-row gap-4 h-full">

			{/* ── Left Sidebar — controls ── */}
			<div className="w-full lg:w-80 shrink-0 bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-4">

				{/* Size */}
				<div>
					<label className="block text-xs font-medium text-gray-500 mb-1">Size</label>
					<select
						value={size}
						onChange={(e) => setSize(e.target.value)}
						className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
					>
						<option value="passport">Passport Size</option>
						<option value="landscape">Passport Landscape (4)</option>
						<option value="stamp">Stamp Size</option>
						<option value="ngo_combined">NGO Combine Photo</option>
						<option value="2x2">2x2 in (Indian Visa Size)</option>
						<option value="nid">NID (2 Image)</option>
						<option value="nid_side_by_side">NID (Front | Back)</option>
						<option value="nid_combined">NID Combined (1 Image)</option>
						<option value="office_id_card">Office ID Card (2 Image)</option>
						<option value="mrp">MRP Passport</option>
						<option value="bordered">A4 with 0.5 Inch Border</option>
						<option value="3R">3R Photo (Album Size)</option>
						<option value="4R">4R Photo (4 Photo)</option>
						<option value="6R">6R Photo (2 Photo)</option>
					</select>
				</div>

				{/* Count */}
				{!isNidSideBySide && (
					<div>
						<label className="block text-xs font-medium text-gray-500 mb-1">Count</label>
						<input
							type="number"
							min={1}
							max={28}
							value={count}
							onChange={(e) => setCount(e.target.value)}
							className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
						/>
					</div>
				)}

				{/* Background color */}
				<div>
					<label className="block text-xs font-medium text-gray-500 mb-2">Background</label>
					<div className="flex flex-wrap gap-1.5 mb-2">
						{presetBGColors.map((color) => (
							<button
								key={color}
								type="button"
								onClick={() => setBgColor(color)}
								title={color}
								className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${bgColor === color ? "border-indigo-500 scale-110" : "border-gray-300"}`}
								style={{ backgroundColor: color }}
							/>
						))}
						<input
							type="color"
							value={bgColor}
							onChange={(e) => setBgColor(e.target.value)}
							title="Custom color"
							className="w-7 h-7 rounded-full border-2 border-gray-300 cursor-pointer p-0.5 focus:outline-none"
						/>
					</div>
				</div>

				{/* Actions — pushed to bottom */}
				<div className="mt-auto flex flex-col gap-2">
					<a
						target="_blank" rel="noreferrer"
						href="https://remove.bg"
						className="w-full text-center px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
					>
						Remove BG ↗
					</a>
					<button
						type="button"
						onClick={handleCreatePdf}
						className="w-full px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors"
					>
						Create PDF
					</button>
				</div>
			</div>

			{/* ── Right — upload area ── */}
			<div className="flex-1 min-w-0 bg-white rounded-xl shadow-sm border border-gray-100 p-3">
				{isNidSideBySide ? (
					<NidPairUploader pairs={nidPairs} onChange={setNidPairs} bgColor={bgColor} />
				) : (
					<ImageDropZone onChange={setFiles} bgColor={bgColor} />
				)}
			</div>

		</div>
	);
};

export default ImageUploader;
